/**
 * --GRID TABLE--
 *
 * header information:
 *  propName: string; Which sort name this header cell is using.
 *  sortProp: string; holds which prop is sorting the table, and gets set by interaction with a header element.
 *  sortOrder: string; holds the sort order, and gets set by interaction with a header element.
 *  children: ; The content rendered in the header cell.
 *  style: [key: string]: string; Override styles
 *
 * row information:
 *
 * table settings:
 *  frozenRows: int[]; freezes rows (like hasStickyHeader) so they aren't hidden by vert scroll. UNIMPLEMENTED
 *  frozenCols: int[]; freezes cols so they aren't hidden by horizontal scroll. UNIMPLEMENTED
 *  sortProp: string; holds which prop is sorting the table.
 *  sortOrder: string; holds whether the sort is asc/desc.
 *  sortFn: Fn; the sortFn function from the parent component.
 *  pageSize: int; Overrides the pageSize selector in the pagination bar and sets a static page size.
 *  pagiantion: Fn; Overrides the default client-side pagination. UNIMPLEMENTED
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { compareDates, compareNumbers, compareStrings } from 'utils/compare';
import { sortingOrder } from 'utils/sort';
import hash from 'object-hash';
import { KakarikoStickyElement } from '../StickyElement';
import { KakarikoProgress } from '../Progress';
import { StyledKakarikoGrid } from './styles';
import type {
  IDomRectKeys,
  IKakarikoGridProps,
  KakarikoGridContextType,
} from './types';
import { retrieveNestedValue } from '../utils';
import { KakarikoPagination } from './Pagination';

const sum = (arr: Array<number>): number => arr.reduce((a, b) => a + b);

export const GridContext = createContext<KakarikoGridContextType>({});

const gridSize = 1000;

const compareFns = {
  string: compareStrings,
  number: compareNumbers,
  date: compareDates,
  objectLabel: (a: { label: string }, b: { label: string }) =>
    compareStrings(a?.label, b?.label),
  objectValue: (a: { value: string }, b: { value: string }) =>
    compareStrings(a?.value, b?.value),
};

export type sortTypeKey = keyof typeof compareFns;

export const KakarikoGrid = (props: IKakarikoGridProps) => {
  const {
    data,
    editMode = 'single',
    hasLastPageSelector,
    header,
    externalDataControl,
    isLoading: propsIsLoading,
    isPaginated,
    currentPage,
    pageSize,
    pageSizeSelectDisabled,
    rowTemplate,
    setPageSize,
    sortOrder,
    setIsLoading: propsSetIsLoading,
    hasStickyHeader = false,
    styles,
    tableTheme = 'light',
    totalRecords,
    variant = 'standard',
    $textAlignment = 'center',
  } = props;

  const sortOrderSequence: (string | undefined)[] = [
    undefined,
    sortingOrder.DESC,
    sortingOrder.ASC,
  ];

  const [dataIndex, setDataIndex] = useState<any[]>([]); // data values with added index for updating

  const [sortedData, setSortedData] = useState<any[]>([]); // raw data values
  const [records, setRecords] = useState<any>([]); // data item to be displayed
  const [rows, setRows] = useState<any>([]); // records after being mapped into a JSX element

  const [sortFieldState, setSortField] =
    useState<string | undefined>(undefined);
  const [sortOrderState, setSortOrder] =
    useState<string | undefined>(undefined);
  const [sortType, setSortType] = useState<sortTypeKey | undefined>();
  const [resetPage, setResetPage] = useState<boolean | undefined>();

  const [isLoading, setIsLoading] = useState(propsIsLoading);
  const [rowIndexEditing, setRowIndexEditing] = useState<undefined | number>();

  // Used to set the grid-template-column value based of the fractionWidth prop on Grid Header Cells.
  const [fractionWidth, setFractionWidth] = useState<any>({
    fractionWidth: [],
  });

  useEffect(() => {
    if (externalDataControl == null) setIsLoading(propsIsLoading || false);
  }, [externalDataControl, propsIsLoading, rows]);

  // Converts the record data into the GridRow based on the rowTemplate.
  useEffect(() => {
    if (records.length && rowTemplate) {
      // Preserves the updateIndex value in order to correctly target data for update.
      const jsxRows = records.map((record: any) => ({
        index: record.updateIndex,
        template: rowTemplate(record),
      }));
      setRows(jsxRows);
    }
  }, [records, rowTemplate]);

  // Adds an index into the raw data object to assist in performing updates when paginated/sorted.
  useEffect(() => {
    setDataIndex(data.map((d, index) => ({ ...d, updateIndex: index })));
  }, [data]);

  // Sets the record data based on the computed data with indices.
  useEffect(() => {
    if (externalDataControl != null) setRecords([...dataIndex]);
    if (externalDataControl?.sortFn) setSortedData([...dataIndex]);
  }, [dataIndex, externalDataControl]);

  useEffect(() => {
    setIsLoading(propsIsLoading);
  }, [propsIsLoading]);

  // If the page is not paginated, we need to set the records immediately, since that is handled by the Pagination
  // component when it is used.
  useEffect(() => {
    if (!isPaginated) setRecords([...sortedData]);
  }, [isPaginated, sortedData]);

  const resetOrderOnFieldChange = useCallback(
    (sortField: string, sortOrderState?: string): string | undefined => {
      if (sortField !== sortFieldState) {
        return undefined;
      }
      return sortOrderState;
    },
    [sortFieldState],
  );

  const getNewSortOrder = useCallback(
    (sortOrder?: string): string | undefined => {
      const sortIndex =
        sortOrderSequence.findIndex((order) => {
          return order === sortOrder;
        }) + 1;
      return sortOrderSequence[sortIndex % sortOrderSequence.length];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setSortProps = useCallback(
    (sortField: string, sortType?: sortTypeKey): void => {
      setIsLoading(true);
      if (!rows.length) return;

      const resetSortOrder = resetOrderOnFieldChange(sortField, sortOrderState);
      const newSortOrder = getNewSortOrder(resetSortOrder);
      setSortOrder(newSortOrder);
      setSortType(sortType);
      setSortField(sortField);
    },
    [rows, sortOrderState, getNewSortOrder, resetOrderOnFieldChange],
  );

  const compare = (
    first?: string | number | object | null,
    second?: string | number | object | null,
    key?: sortTypeKey,
  ) => {
    if (typeof first !== typeof second) return 0;
    const compareKey =
      key ?? ((first === null ? 'string' : typeof first) as sortTypeKey);
    return compareKey in compareFns ? compareFns[compareKey](first, second) : 0;
  };

  const sortData = useCallback(
    (array: any) => {
      if (!sortFieldState || !sortOrderState) return array;
      if (externalDataControl) return array;

      const sortedArray = array.sort((a: any, b: any) => {
        const order = compare(
          retrieveNestedValue(sortFieldState, a),
          retrieveNestedValue(sortFieldState, b),
          sortType,
        );
        if (sortOrderState === sortingOrder.ASC) return order;
        if (sortOrderState === sortingOrder.DESC) return -order;
        return order;
      });

      return sortedArray;
    },
    [sortFieldState, sortOrderState, sortType, externalDataControl],
  );

  useEffect(() => {
    if (sortOrder) setSortOrder(sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    const callSort = async () => {
      if (externalDataControl) {
        await externalDataControl.sortFn(sortFieldState, sortOrderState);
      } else {
        if (sortFieldState != null || !sortOrderState) {
          setSortedData(sortData([...(dataIndex ?? [])]));
        } else {
          setSortedData([...(dataIndex ?? [])]);
        }
        setResetPage((r) => !r);
      }
    };
    callSort();
  }, [
    sortFieldState,
    sortOrderState,
    dataIndex,
    externalDataControl,
    sortData,
  ]);

  const stickyRef = useRef<Element | undefined>();

  const headerJSX = useMemo(
    () =>
      React.cloneElement(header, {
        key: 'header',
        tableSortField: sortFieldState,
        sortOrder: sortOrderState,
        setSortProps,
        tableSortable: !!rows.length,
        theme: tableTheme,
        ref: stickyRef,
      }),
    [
      header,
      rows.length,
      sortFieldState,
      sortOrderState,
      tableTheme,
      setSortProps,
    ],
  );

  const calcPos = useCallback(
    (staticEdge: 'top' | 'left') => {
      if (!stickyRef || !stickyRef.current) return null;
      if (!stickyRef.current.children)
        return stickyRef.current.getBoundingClientRect();
      const rect: IDomRectKeys = {
        height: null,
        left: null,
        top: null,
        width: null,
      };
      [...stickyRef.current.children].forEach((child) => {
        const childRect = child.getBoundingClientRect();
        rect.height =
          staticEdge === 'top'
            ? sum([rect.height ?? 0, childRect.height]) / 2
            : sum([rect.height ?? 0, childRect.height]);
        rect.left =
          staticEdge === 'top' && rect.left === null
            ? rect.left
            : sum([rect.left ?? 0, childRect.left]) / 2;
        rect.top =
          staticEdge === 'left' && rect.top === null
            ? childRect.top
            : sum([rect.top ?? 0, childRect.top]) / 2;
        rect.width =
          staticEdge === 'top'
            ? sum([rect.width ?? 0, childRect.width])
            : sum([rect.width ?? 0, childRect.width]) / 2;
      });
      return rect;
    },
    [stickyRef],
  );

  const updateDataRow = useCallback(
    (index: number) => {
      return (rowData: any) => {
        const newData = [...dataIndex];
        newData[index] = rowData;
        setDataIndex(newData);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dataIndex],
  );

  const grid = useCallback(
    (children: React.ReactElement | React.ReactElement[], key: string) => (
      <StyledKakarikoGrid
        key={key}
        className={'kakariko-grid'}
        style={styles}
        variant={variant}
        $fractionWidth={fractionWidth.fractionWidth}
        $tableTheme={tableTheme}
      >
        {children}
      </StyledKakarikoGrid>
    ),
    [fractionWidth.fractionWidth, styles, tableTheme, variant],
  );

  const setGridRows = useCallback(
    (v: number) => {
      return [
        ...rows
          .slice(v, v + gridSize)
          .map(
            (
              row: { index: number; template: React.ReactElement },
              n: number,
            ) => {
              const singleEditMode = editMode === 'single';
              let isEditingRow = rowIndexEditing === n;
              const editUpdate = (state?: boolean) =>
                setRowIndexEditing(state != null && !state ? undefined : n);
              return React.cloneElement(row.template, {
                key: `${hash(row.template.props?.data, { algorithm: 'md5' })}`,
                $textAlignment,
                ...(singleEditMode && {
                  isEditing: isEditingRow,
                  handleEditingState: (state?: boolean) => editUpdate(state),
                }),
                updateDataRow: updateDataRow(row.index),
                ...row.template.props,
              });
            },
          ),
      ];
    },
    [rows, $textAlignment, editMode, rowIndexEditing, updateDataRow],
  );

  const numberOfGrids = useMemo(
    () => Math.ceil(rows.length / gridSize),
    [rows],
  );

  const newGrids = useCallback(() => {
    const grids = [];
    for (let i = 1; i < numberOfGrids; i++) {
      grids.push(
        <StyledKakarikoGrid
          key={`data-grid-${i}`}
          className={'kakariko-grid'}
          style={styles}
          variant={variant}
          $fractionWidth={fractionWidth.fractionWidth}
          $tableTheme={tableTheme}
        >
          {setGridRows(i * gridSize)}
        </StyledKakarikoGrid>,
      );
    }
    return grids;
  }, [
    fractionWidth.fractionWidth,
    numberOfGrids,
    styles,
    tableTheme,
    variant,
    setGridRows,
  ]);

  return (
    <GridContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        isLoading: propsIsLoading,
        setIsLoading: propsSetIsLoading,
        tableTheme,
        variant,
        setFractionWidth,
        setRecords,
        // Pagination
        isFirstPage: externalDataControl?.isFirstPage,
        isLastPage: externalDataControl?.isLastPage,
        handleClickFirstPage: externalDataControl?.handleClickFirstPage,
        handleClickLastPage: externalDataControl?.handleClickLastPage,
        handleClickPrevPage: externalDataControl?.handleClickPrevPage,
        handleClickNextPage: externalDataControl?.handleClickNextPage,
        setPageSize,
        // Sorting
        sortFieldState,
        sortOrderState,
        setSortProps,
        sortFn: externalDataControl?.sortFn,
      }}
    >
      {isPaginated && (
        <KakarikoPagination
          currentPage={currentPage}
          data={sortedData}
          hasLastPageSelector={hasLastPageSelector}
          pageSize={pageSize}
          pageSizeSelectDisabled={pageSizeSelectDisabled}
          resetPage={resetPage}
          totalRecords={totalRecords}
        />
      )}
      <StyledKakarikoGrid
        key={isLoading ? 'loader-grid' : 'data-grid'}
        className={'kakariko-grid'}
        style={styles}
        variant={variant}
        $fractionWidth={fractionWidth.fractionWidth}
        $tableTheme={tableTheme}
      >
        <>
          {headerJSX}
          {hasStickyHeader && !isLoading && (
            <KakarikoStickyElement
              key="header-sticky"
              positionOverride={() => calcPos('top')}
            >
              {grid(
                React.cloneElement(headerJSX, { ref: null }),
                'sticky-header-grid',
              )}
            </KakarikoStickyElement>
          )}
          {isLoading ? (
            <KakarikoProgress key={'loader'} size={40} />
          ) : (
            setGridRows(0)
          )}
        </>
      </StyledKakarikoGrid>
      {numberOfGrids > 1 && !isLoading && newGrids()}
    </GridContext.Provider>
  );
};
